import { useMutation } from '@tanstack/react-query';
import type React from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchParams, SetupState, StorageKeys } from '../../api/constants';
import * as api from '../../api/setupServices';
import { CheckDpaConsentResponseActionType } from '../../api/types';
import { ErrorCode } from '../../errorHandler/constants';
import { useErrorContext } from '../../errorHandler/context/ErrorContext';
import { type ApiError, AppError, FatalError } from '../../errorHandler/errors';
import { initializePendo } from '../../utils/pendo';
import { PENDO_CUSTOMER_ADMIN } from '../constants';

type UseStartTenantSetupProps = {
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setDpaNoActionNeeded: React.Dispatch<React.SetStateAction<boolean>>;
};

const useStartTenantSetup = ({ setLoading, setDpaNoActionNeeded }: UseStartTenantSetupProps) => {
  const [searchParams] = useSearchParams();
  const { emitError } = useErrorContext();

  return useMutation({
    mutationFn: async () => {
      const bearerToken = searchParams.get(SearchParams.BEARER_TOKEN);
      await api.startTenantSetup(bearerToken);
    },
    onSuccess: async () => {
      console.log('Tenant setup started');
      sessionStorage.setItem(StorageKeys.TENANT_SETUP_STATE, SetupState.STARTED);
      //Initialize Pendo
      initializePendo(searchParams.get(SearchParams.TENANT_ID) || '', {
        username: PENDO_CUSTOMER_ADMIN,
        roleType: 'admin',
      });
      setLoading(true);
      try {
        const res = await api.dpaCheck();
        sessionStorage.setItem(StorageKeys.DPA_DOCUMENT_NAME, res.documentName);
        sessionStorage.setItem(StorageKeys.DPA_DOCUMENT_URL, res.documentUrl);
        setDpaNoActionNeeded(res.actionType === CheckDpaConsentResponseActionType.NONE);
      } catch (e) {
        emitError(new FatalError(e as ApiError));
      } finally {
        setLoading(false);
      }
    },
    onError: (e: unknown) => {
      console.log('Tenant setup failed', e);
      if (typeof e !== 'object' || e === null) {
        return emitError(new FatalError({ code: ErrorCode.UNKNOWN, message: String(e) }));
      }

      const err = e as ApiError;

      if (err.code === ErrorCode.INVALID_TENANT_STATE && err.data?.TENANT_STATE === 'ENABLED') {
        const appError = e as AppError;
        appError.code = ErrorCode.LINK_DEAD;
        emitError(appError);
      } else if (err.code === ErrorCode.TOKEN_EXPIRED) {
        emitError(new AppError(err));
      } else {
        const apiError = e as ApiError;
        emitError(new FatalError(apiError));
      }
    },
  });
};

export default useStartTenantSetup;
